import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CheckCircle, Home, ArrowRight, FileText, Phone, Calendar, Shield, Check } from 'lucide-react';
import Header from '../../components/ui/Header';

const nextSteps = [
  {
    icon: <Phone size={18} />,
    title: 'Confirmation par téléphone',
    description: 'Un conseiller Kapital States vous contactera sous 24h pour confirmer votre réservation et vous guider.',
    delay: '0ms',
    accent: 'text-blue-400',
    bg: 'bg-blue-500/10'
  },
  {
    icon: <Calendar size={18} />,
    title: 'Rendez-vous notaire',
    description: 'Nous programmerons un rendez-vous avec un notaire certifié pour la signature des actes officiels.',
    delay: '80ms',
    accent: 'text-cyan-400',
    bg: 'bg-cyan-500/10'
  },
  {
    icon: <Shield size={18} />,
    title: 'Fonds en séquestre sécurisé',
    description: 'Votre paiement est conservé en séquestre et ne sera débloqué qu\'après validation notariale complète.',
    delay: '160ms',
    accent: 'text-emerald-400',
    bg: 'bg-emerald-500/10'
  },
  {
    icon: <FileText size={18} />,
    title: 'Documents contractuels',
    description: 'Vous recevrez par email tous les documents contractuels à vérifier avant la signature finale.',
    delay: '240ms',
    accent: 'text-purple-400',
    bg: 'bg-purple-500/10'
  },
];

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const reference = searchParams?.get('reference') || searchParams?.get('trxref') || searchParams?.get('session_id') || null;
  const propertyTitle = searchParams?.get('property') || 'Bien immobilier';
  const amount = searchParams?.get('amount') || null;
  const propertyId = searchParams?.get('property_id') || null;

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const formattedAmount = amount
    ? new Intl.NumberFormat('fr-CI', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })?.format(Number(amount))
    : null;

  return (
    <>
      <Helmet>
        <title>Paiement Confirmé – Kapital States</title>
        <meta name="description" content="Votre paiement a été confirmé avec succès. Découvrez les prochaines étapes de votre acquisition immobilière." />
      </Helmet>

      {/* Fond Noir Absolu, Zéro Bordure */}
      <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden border-none outline-none flex flex-col">
        <Header className="bg-transparent border-none" />

        {/* Ambient glow de succès (Émeraude) */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden flex items-start justify-center" aria-hidden="true">
          <div className="w-[800px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] -translate-y-1/3" />
        </div>

        <main className="relative z-10 flex-grow max-w-3xl mx-auto px-6 py-20 w-full flex flex-col justify-center">

          {/* En-tête de succès */}
          <div
            className="flex flex-col items-center text-center mb-16"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <div className="w-24 h-24 bg-emerald-500/10 rounded-3xl flex items-center justify-center mb-8 shadow-lg shadow-emerald-500/20">
              <CheckCircle size={48} className="text-emerald-400" strokeWidth={1.5} />
            </div>
            
            <span className="text-xs font-bold tracking-widest uppercase mb-4 text-emerald-400">
              Paiement confirmé
            </span>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold mb-6 leading-tight tracking-tight text-white">
              Votre réservation est{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300">
                sécurisée.
              </span>
            </h1>
            
            <p className="text-white/50 text-lg max-w-lg mx-auto font-light leading-relaxed">
              Merci pour votre confiance. Votre paiement a été traité avec succès et vos fonds sont placés en séquestre sécurisé.
            </p>
          </div>

          {/* Carte de Récapitulatif (Glassmorphism Sombre) */}
          <div
            className="bg-[#111111] rounded-3xl mb-12 shadow-2xl border-none outline-none overflow-hidden relative"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s 0.1s cubic-bezier(0.16,1,0.3,1), transform 0.7s 0.1s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            {/* Petit filet lumineux en haut de la carte */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />

            {/* En-tête de la carte */}
            <div className="px-8 py-5 bg-white/[0.02] flex items-center gap-4">
              <FileText size={18} className="text-emerald-400" />
              <span className="text-base font-heading font-semibold text-white">Récapitulatif de commande</span>
            </div>

            <div className="px-8 py-6 space-y-6">
              {/* Propriété */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-widest font-bold mb-2">Bien immobilier</p>
                  <p className="text-white font-medium text-lg leading-snug">{propertyTitle}</p>
                </div>
                {propertyId && (
                  <Link
                    to={`/property/${propertyId}`}
                    className="flex-shrink-0 text-xs px-4 py-2 rounded-full font-bold bg-white/5 text-white hover:bg-white/10 transition-colors border-none"
                  >
                    Voir le bien
                  </Link>
                )}
              </div>

              {/* Ligne de séparation */}
              <div className="h-px bg-white/5 w-full" />

              {/* Montant & Référence */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {formattedAmount && (
                  <div>
                    <p className="text-xs text-white/40 uppercase tracking-widest font-bold mb-2">Montant réglé</p>
                    <p className="text-2xl font-bold text-emerald-400">{formattedAmount}</p>
                  </div>
                )}
                
                {reference && (
                  <div>
                    <p className="text-xs text-white/40 uppercase tracking-widest font-bold mb-2">Référence</p>
                    <p className="text-sm font-mono text-white/70 bg-white/5 px-3 py-1.5 rounded-lg inline-block">
                      {reference}
                    </p>
                  </div>
                )}
              </div>

              {/* Statut & Avertissement Séquestre */}
              <div className="pt-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-widest font-bold mb-2">Statut des fonds</p>
                  <span className="inline-flex items-center gap-2 text-sm font-bold bg-emerald-500/10 text-emerald-400 px-4 py-2 rounded-full border-none shadow-sm shadow-emerald-500/10">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    En séquestre (Confirmé)
                  </span>
                </div>
                
                <div className="flex-1 bg-white/5 rounded-2xl p-4 flex items-start gap-4">
                  <Shield size={20} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-white/50 leading-relaxed font-light">
                    Les fonds ne seront débloqués au vendeur qu'<strong className="text-white/80 font-medium">après validation complète</strong> par votre notaire.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Prochaines étapes */}
          <div
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s 0.2s cubic-bezier(0.16,1,0.3,1), transform 0.7s 0.2s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <h2 className="text-sm font-bold text-white/40 uppercase tracking-widest mb-6">Prochaines étapes</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {nextSteps?.map((step, i) => (
                <div
                  key={i}
                  className="bg-[#111111] rounded-2xl p-5 flex items-start gap-4 border-none shadow-xl transition-all hover:-translate-y-1"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(12px)',
                    transition: `all 0.5s ${step?.delay} cubic-bezier(0.16,1,0.3,1)`,
                  }}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${step.bg} ${step.accent}`}>
                    {step?.icon}
                  </div>
                  <div>
                    <p className="text-base font-bold text-white mb-1.5">{step?.title}</p>
                    <p className="text-xs text-white/50 leading-relaxed font-light">{step?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Boutons d'action */}
          <div
            className="flex flex-col sm:flex-row gap-4 mt-12"
            style={{
              opacity: visible ? 1 : 0,
              transition: 'opacity 0.7s 0.4s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <Link
              to="/mon-espace"
              className="flex-1 flex items-center justify-center gap-3 py-4 px-6 rounded-full font-bold text-base transition-transform hover:scale-105 active:scale-95 bg-white text-black shadow-xl"
            >
              Aller à mon Espace
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/property-search-listings"
              className="flex-1 flex items-center justify-center gap-3 py-4 px-6 rounded-full font-bold text-base transition-transform hover:scale-105 active:scale-95 bg-white/5 hover:bg-white/10 text-white border-none"
            >
              <Home size={18} />
              Explorer d'autres biens
            </Link>
          </div>

          {/* Support */}
          <p className="text-center text-sm font-light text-white/30 mt-12 mb-8">
            Besoin d'assistance ? Contactez-nous à{' '}
            <a href="mailto:contact@kapitalstates.com" className="text-white/60 hover:text-white font-medium transition-colors no-underline">
              contact@kapitalstates.com
            </a>
          </p>

        </main>
      </div>
    </>
  );
};

export default PaymentSuccess;